import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button";
import Drawer from "@via/components/drawer";
import { GenericError } from "@via/components/error";
import Table, { TableSize } from "@via/components/table/table";
import { BUTTONS, ERROR, INTEGRATION_TOKEN_TABLE } from "@via/utils/constants";
import { formatDateString } from "@via/utils/helpers";
import { useEffect, useState } from "react";
import {
  useIntegrationTokens,
  useSelectIntegrationToken,
} from "@via/api/integration-tokens.api";
import useUrlParams from "@via/hooks/useURLParams";
import IntegrationTokenFilter from "@via/pages/account/profile/integration-tokens/components/integration-token-filter";
import DeleteIntegrationTokenModal from "@via/pages/account/profile/integration-tokens/components/delete-integration-token-modal";
import { getTokenTypeIcon } from "@via/pages/account/profile/integration-tokens/utils/helper";
import CreateIntegrationTokenForm from "@via/pages/account/profile/integration-tokens/components/create-integration-token-form";
import EditIntegrationTokenForm from "@via/pages/account/profile/integration-tokens/components/edit-integration-token-form";
import TableContextMenu from "@via/components/table/table-context-menu";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { Badge } from "@via/components/badge";
import { useAuth } from "@via/hooks/useAuth";
import { ServiceTokenListItem } from "common";
import CustomInstructions from "@via/pages/account/profile/integration-tokens/custom-instructions/custom-instructions";

const columnHelper = createColumnHelper<ServiceTokenListItem>();

const defaultParams = {
  sort: [{ id: "createdAt", desc: true }],
  filters: [],
  page: 0,
  pageSize: 10,
};

const IntegrationTokens = () => {
  const { user } = useAuth();
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [integrationTokenToBeDeleted, setIntegrationTokenToBeDeleted] =
    useState<ServiceTokenListItem | null>(null);
  const [integrationTokenToBeEdited, setIntegrationTokenToBeEdited] =
    useState<ServiceTokenListItem | null>(null);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(initialParams.filters);

  const { data, isLoading, isError } = useIntegrationTokens(
    page,
    pageSize,
    sort,
    columnFilters
  );

  const {
    mutateAsync: selectIntegrationToken,
    isPending: isSelectIntegrationTokenLoading,
  } = useSelectIntegrationToken();

  useEffect(() => {
    setUrlParams({
      sort,
      filters: columnFilters,
      page,
      pageSize,
    });
  }, [sort, columnFilters, page, pageSize]);

  const handleAction = (
    option: TableContextMenuInterface,
    row: ServiceTokenListItem
  ): void => {
    switch (option.name) {
      case "Delete":
        setIntegrationTokenToBeDeleted(row);
        break;
      case "Edit":
        setIntegrationTokenToBeEdited(row);
        setIsEditDrawerOpen(true);
        break;
      case "Select":
        selectIntegrationToken(row.id.toString());
        break;
      default:
        break;
    }
  };

  const columns = [
    columnHelper.accessor("id", {
      cell: info => info.getValue(),
      header: () => <span>Id</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("tokenType", {
      cell: info => (
        <img
          className="w-5 py-2"
          alt={info.getValue()}
          src={getTokenTypeIcon(info.getValue())}
        />
      ),
      header: () => <span>Token Type</span>,
    }),
    columnHelper.accessor("name", {
      cell: info => info.getValue(),
      header: () => <span>Token Name</span>,
    }),
    columnHelper.accessor("ownerEmail", {
      cell: info => info.getValue(),
      header: () => <span>Owner</span>,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LLL")}</span>;
      },
      header: () => <span>Created At</span>,
    }),
    columnHelper.accessor("selected", {
      cell: info => {
        const value = info.getValue();
        if (value) {
          return (
            <Badge
              text="Selected"
              textColor="text-green-500"
              fillColor="fill-green-500"
            />
          );
        }
        return <></>;
      },
      header: () => <span>Selected</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("id", {
      cell: info => {
        const selected = info.row.original.selected;
        const options = [
          ...INTEGRATION_TOKEN_TABLE,
          ...(selected === false ? [{ name: "Select", href: "#" }] : []),
        ];
        return (
          <TableContextMenu
            options={options}
            onSelect={option => handleAction(option, info.row.original)}
          />
        );
      },
      header: () => <span>Options</span>,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<ServiceTokenListItem>>;

  return (
    <>
      <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
        <div className="via-page-heading-wrapper">
          <h2 className="via-h2">Integration Tokens</h2>
          <ActionButton
            button={BUTTONS.CREATE_TOKEN}
            onClick={() => {
              setIsCreateDrawerOpen(true);
            }}
          />
        </div>
        {isError ? (
          <div className="via-generic-error">
            <GenericError message={ERROR} />
          </div>
        ) : (
          <Table
            isLoading={isLoading || isSelectIntegrationTokenLoading}
            data={data?.data}
            columns={columns}
            sort={sort}
            setSort={setSort}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total={data?.total}
            size={TableSize.Big}
            additionalFilters={
              <IntegrationTokenFilter
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                setPage={setPage}
                setPageSize={setPageSize}
              />
            }
          />
        )}
        {integrationTokenToBeDeleted && (
          <DeleteIntegrationTokenModal
            integrationToken={integrationTokenToBeDeleted}
            setIntegrationTokenToBeDeleted={() => {
              setIntegrationTokenToBeDeleted(null);
            }}
          />
        )}
        <Drawer
          id="create-integration-token"
          open={isCreateDrawerOpen}
          setOpen={setIsCreateDrawerOpen}>
          <CreateIntegrationTokenForm setOpen={setIsCreateDrawerOpen} />
        </Drawer>
        <Drawer
          id="edit-integration-token"
          open={isEditDrawerOpen}
          setOpen={setIsEditDrawerOpen}>
          <EditIntegrationTokenForm
            setOpen={setIsEditDrawerOpen}
            integrationToken={integrationTokenToBeEdited}
          />
        </Drawer>
      </div>
      {user?.featureFlags?.FF_AI_COPILOT && <CustomInstructions />}
    </>
  );
};

export default IntegrationTokens;
